/* eslint-disable no-console */
import type { DocumentNode } from 'graphql';

import { getOperationName } from './getOperationName.ts';
import { getQueryName } from './getQueryName.ts';

const preloadedQueries: Map<string, Set<string>> = new Map();
const suspenseBoundaryErrors: Set<string> = new Set();

function isBoundaryVerificationEnabled() {
	return (
		process.env.CLOUD_ENV === 'dev' ||
		process.env.CLOUD_ENV === 'branch' ||
		process.env.CLOUD_ENV === 'staging'
	);
}

function emitQueryBoundaryError(
	operationName: string,
	errorType: 'outside-boundary' | 'boundary-mismatch',
	args: {
		expectedBoundaries?: string[];
		currentBoundaries?: string[];
		boundaryName?: string;
	},
) {
	const errorId = `${errorType}-${operationName}`;

	if (suspenseBoundaryErrors.has(errorId)) {
		return;
	}

	const { getServerStream } = require('@confluence/ssr-data-streaming/entry-points/server.ts');
	const errorStream = getServerStream('query-boundary-errors');
	errorStream.emit({ operationName, errorType, args });
	suspenseBoundaryErrors.add(errorId);
}

export function trackPreloadedQuery(
	query: DocumentNode,
	variables: any,
	fetchBoundaryName: string,
): void {
	if (isBoundaryVerificationEnabled() && window.__SSR_REACT_STREAMING__) {
		const operationName = getOperationName(query);
		const queryKey = getQueryName(operationName, variables, true);

		if (fetchBoundaryName) {
			if (!preloadedQueries.has(queryKey)) {
				preloadedQueries.set(queryKey, new Set());
			}
			const boundaries = preloadedQueries.get(queryKey)!;
			boundaries.add(fetchBoundaryName);
		}
	}
}

export function verifyQueryBoundary(
	query: DocumentNode,
	variables: any,
	currentSuspenseBoundaries: string[],
): void {
	if (isBoundaryVerificationEnabled() && window.__SSR_REACT_STREAMING__) {
		const operationName = getOperationName(query);
		const queryKey = getQueryName(operationName, variables, true);
		const preloadedFetchBoundaries = preloadedQueries.get(queryKey);

		if (
			!operationName ||
			!preloadedFetchBoundaries ||
			Array.from(preloadedFetchBoundaries).includes('default')
		) {
			return;
		}

		if (preloadedFetchBoundaries && currentSuspenseBoundaries.length === 0) {
			console.error(
				`🚨 Query used outside suspense boundary!\n` +
					`Query: ${operationName}\n` +
					`Variables: ${JSON.stringify(variables, null, 2)}\n` +
					`Expected suspense boundaries: ${Array.from(preloadedFetchBoundaries).join(', ')}\n` +
					`Current suspense boundary: none\n` +
					`This query was preloaded in boundaries [${Array.from(preloadedFetchBoundaries).join(', ')}] but is being accessed outside of any suspense boundary. ` +
					`Consider wrapping the component in a SuspenseWithDataFetcher with one of these dataFetcherNames: ${Array.from(preloadedFetchBoundaries).join(', ')}.`,
			);

			emitQueryBoundaryError(operationName, 'outside-boundary', {
				expectedBoundaries: Array.from(preloadedFetchBoundaries),
				currentBoundaries: currentSuspenseBoundaries,
			});
		} else if (preloadedFetchBoundaries) {
			const hasValidAccess = currentSuspenseBoundaries.some((boundaryInChain) =>
				preloadedFetchBoundaries.has(boundaryInChain),
			);

			if (!hasValidAccess) {
				console.error(
					`🚨 Query boundary mismatch detected!\n` +
						`Query: ${operationName}\n` +
						`Variables: ${JSON.stringify(variables, null, 2)}\n` +
						`Expected suspense boundaries: ${Array.from(preloadedFetchBoundaries).join(', ')}\n` +
						`Current suspense boundaries: ${currentSuspenseBoundaries.join(' → ')}\n` +
						`This query was preloaded in boundaries [${Array.from(preloadedFetchBoundaries).join(', ')}], ` +
						`but is being accessed in the "${currentSuspenseBoundaries.join(' → ')}" boundary. ` +
						`Child boundaries can inherit data from parent boundaries, but none of the boundaries in the chain can access this data.`,
				);

				emitQueryBoundaryError(operationName, 'boundary-mismatch', {
					expectedBoundaries: Array.from(preloadedFetchBoundaries),
					currentBoundaries: currentSuspenseBoundaries,
				});
			}
		}
	}
}

export function getTrackedQueries(): Map<string, Set<string>> | null {
	if (isBoundaryVerificationEnabled()) {
		return new Map(preloadedQueries);
	}
	return null;
}

export function clearQueryBoundaryTracking(): void {
	if (isBoundaryVerificationEnabled()) {
		preloadedQueries.clear();
		suspenseBoundaryErrors.clear();
	}
}
